// ReactionSummaryChips — compact, read-only display of a card's emoji
// reactions. Reacting itself happens only in the detail view; this just shows
// what the reaction bar over there already recorded.
//
// It renders the most-reacted emoji (the summary arrives popularity-ordered
// from the API) with its count, up to a small cap, then a "+N" pill counting
// the remaining *distinct* emoji so a busy card stays one tidy line.
import type { ReactElement } from "react";
import type { ReactionSummary } from "../api";

const MAX_VISIBLE = 3;

export interface ReactionSummaryChipsProps {
  reactions: ReactionSummary[];
}

export function ReactionSummaryChips({
  reactions,
}: ReactionSummaryChipsProps): ReactElement | null {
  if (!reactions || reactions.length === 0) return null;

  const visible = reactions.slice(0, MAX_VISIBLE);
  const overflow = reactions.length - visible.length;
  const totalReactors = reactions.reduce((sum, r) => sum + r.count, 0);

  return (
    <span
      className="fr-reactions"
      data-testid="feed-reactions"
      aria-label={`${totalReactors} ${totalReactors === 1 ? "reaction" : "reactions"}`}
    >
      {visible.map((r) => (
        <span
          key={r.emoji}
          className={"fr-reaction" + (r.reacted_by_me ? " fr-reaction--mine" : "")}
        >
          <span aria-hidden="true">{r.emoji}</span>
          <span className="fr-reaction__count">{r.count}</span>
        </span>
      ))}
      {overflow > 0 && (
        <span className="fr-reaction fr-reaction--more" aria-label={`${overflow} more`}>
          +{overflow}
        </span>
      )}
    </span>
  );
}

export default ReactionSummaryChips;
